import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clientsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Save, Search } from 'lucide-react';

const PREFERRED_CHANNELS = ['whatsapp', 'email', 'phone'];
const SERVICE_MODES = ['walk_in', 'domestic_remote', 'international'];
const COMPLEXION_UNDERTONES = ['warm', 'cool', 'neutral'];
const COMPLEXION_DEPTHS = ['light', 'medium', 'deep'];
const FACE_SHAPES = ['oval', 'round', 'square', 'oblong', 'heart', 'diamond', 'triangular'];
const BODY_SHAPES = ['athletic', 'slim', 'stocky', 'tall_lean', 'heavy_set', 'proportional'];
const COLOUR_PREFS = ['bright', 'pastel', 'earth_tones', 'neutrals', 'monochrome', 'adventurous'];
const PATTERN_COMFORTS = ['solids_only', 'subtle_textures', 'small_checks', 'bold_patterns'];

const EMPTY_FORM = {
  first_name: '', last_name: '', email: '', phone: '', whatsapp_number: '',
  preferred_channel: 'whatsapp', location_city: '', location_country: '',
  service_mode: 'walk_in', date_of_birth: '', anniversary_date: '',
  referred_by_client_id: '', style_notes: '',
  complexion_undertone: '', complexion_depth: '', face_shape: '', body_shape: '',
  colour_preference: '', pattern_comfort: '', notes: '',
};

export default function ClientFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id && id !== 'new';

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Referral search
  const [refSearch, setRefSearch] = useState('');
  const [refResults, setRefResults] = useState([]);
  const [refName, setRefName] = useState('');
  const [refDropdownOpen, setRefDropdownOpen] = useState(false);

  useEffect(() => {
    if (isEdit) {
      clientsAPI.get(id).then(r => {
        const c = r.data;
        setForm({
          first_name: c.first_name || '', last_name: c.last_name || '',
          email: c.email || '', phone: c.phone || '', whatsapp_number: c.whatsapp_number || '',
          preferred_channel: c.preferred_channel || 'whatsapp',
          location_city: c.location_city || '', location_country: c.location_country || '',
          service_mode: c.service_mode || 'walk_in',
          date_of_birth: c.date_of_birth || '', anniversary_date: c.anniversary_date || '',
          referred_by_client_id: c.referred_by_client_id || '',
          style_notes: c.style_notes || '',
          complexion_undertone: c.complexion_undertone || '',
          complexion_depth: c.complexion_depth || '',
          face_shape: c.face_shape || '', body_shape: c.body_shape || '',
          colour_preference: c.colour_preference || '', pattern_comfort: c.pattern_comfort || '',
          notes: c.notes || '',
        });
        if (c.referred_by_name) setRefName(c.referred_by_name);
        setLoading(false);
      }).catch(() => {
        toast.error('Failed to load client');
        navigate('/app/clients');
      });
    }
  }, [id, isEdit, navigate]);

  // Referral search with debounce
  useEffect(() => {
    if (!refSearch || refSearch.length < 2) { setRefResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await clientsAPI.search(refSearch);
        // Filter out the current client being edited
        setRefResults(r.data.filter(c => c.id !== id));
      } catch { setRefResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [refSearch, id]);

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.last_name.trim()) e.last_name = 'Required';
    if (!form.phone.trim()) e.phone = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // Clean nullables
      for (const key of Object.keys(payload)) {
        if (payload[key] === '') payload[key] = null;
      }
      // Keep required fields
      payload.first_name = form.first_name;
      payload.last_name = form.last_name;
      payload.phone = form.phone;
      payload.preferred_channel = form.preferred_channel || 'whatsapp';
      payload.service_mode = form.service_mode || 'walk_in';

      if (isEdit) {
        await clientsAPI.update(id, payload);
        toast.success('Client updated');
        navigate(`/app/clients/${id}`);
      } else {
        const response = await clientsAPI.create(payload);
        toast.success('Client created');
        navigate(`/app/clients/${response.data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const selectReferral = (client) => {
    updateField('referred_by_client_id', client.id);
    setRefName(client.name);
    setRefSearch('');
    setRefResults([]);
    setRefDropdownOpen(false);
  };

  const clearReferral = () => {
    updateField('referred_by_client_id', '');
    setRefName('');
    setRefSearch('');
  };

  if (loading) {
    return (<div className="space-y-6"><Skeleton className="h-10 w-48" /><Card className="rounded-xl"><CardContent className="p-6 space-y-4">
      {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
    </CardContent></Card></div>);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(isEdit ? `/app/clients/${id}` : '/app/clients')} className="h-9 px-2">
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-semibold" style={{ color: 'var(--brand-navy)' }}>
            {isEdit ? 'Edit Client' : 'Add New Client'}
          </h1>
        </div>
        <Button data-testid="client-save-button" onClick={handleSave} disabled={saving}
          className="h-11 rounded-xl px-6 text-sm font-medium" style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Required Info */}
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Basic Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>First Name *</Label>
                  <Input data-testid="client-first-name" value={form.first_name} onChange={(e) => updateField('first_name', e.target.value)} placeholder="First name" className="h-11 rounded-lg" />
                  {errors.first_name && <p className="text-xs" style={{ color: 'var(--danger)' }}>{errors.first_name}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Last Name *</Label>
                  <Input data-testid="client-last-name" value={form.last_name} onChange={(e) => updateField('last_name', e.target.value)} placeholder="Last name" className="h-11 rounded-lg" />
                  {errors.last_name && <p className="text-xs" style={{ color: 'var(--danger)' }}>{errors.last_name}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Phone *</Label>
                  <Input data-testid="client-phone" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} placeholder="+91 98xxx xxxxx" className="h-11 rounded-lg" />
                  {errors.phone && <p className="text-xs" style={{ color: 'var(--danger)' }}>{errors.phone}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Email</Label>
                  <Input data-testid="client-email" type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} placeholder="email@example.com" className="h-11 rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>WhatsApp Number</Label>
                  <Input value={form.whatsapp_number} onChange={(e) => updateField('whatsapp_number', e.target.value)} placeholder="If different from phone" className="h-11 rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Preferred Channel</Label>
                  <Select value={form.preferred_channel || 'whatsapp'} onValueChange={(v) => updateField('preferred_channel', v)}>
                    <SelectTrigger className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PREFERRED_CHANNELS.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Location & Service */}
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Location & Service</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>City</Label>
                  <Input value={form.location_city} onChange={(e) => updateField('location_city', e.target.value)} placeholder="Mumbai" className="h-11 rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Country</Label>
                  <Input value={form.location_country} onChange={(e) => updateField('location_country', e.target.value)} placeholder="India" className="h-11 rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Service Mode</Label>
                  <Select value={form.service_mode || 'walk_in'} onValueChange={(v) => updateField('service_mode', v)}>
                    <SelectTrigger className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="walk_in">Walk-in</SelectItem>
                      <SelectItem value="domestic_remote">Domestic Remote</SelectItem>
                      <SelectItem value="international">International</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dates */}
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Personal Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Date of Birth</Label>
                  <Input type="date" value={form.date_of_birth} onChange={(e) => updateField('date_of_birth', e.target.value)} className="h-11 rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Anniversary Date</Label>
                  <Input type="date" value={form.anniversary_date} onChange={(e) => updateField('anniversary_date', e.target.value)} className="h-11 rounded-lg" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Notes</Label>
                <textarea value={form.notes} onChange={(e) => updateField('notes', e.target.value)}
                  className="w-full h-24 px-3 py-2 rounded-lg border border-border/80 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:ring-offset-2" style={{ backgroundColor: 'white' }}
                  placeholder="General notes about this client..." />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Referral */}
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Referral</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {form.referred_by_client_id && refName ? (
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Referred By</p>
                    <p className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>{refName}</p>
                  </div>
                  <button onClick={clearReferral} className="text-xs underline" style={{ color: 'var(--danger)' }}>Remove</button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                    <Input
                      data-testid="client-referral-search"
                      placeholder="Search client to link..."
                      value={refSearch}
                      onChange={(e) => { setRefSearch(e.target.value); setRefDropdownOpen(true); }}
                      onFocus={() => setRefDropdownOpen(true)}
                      className="h-11 pl-10 rounded-lg"
                    />
                  </div>
                  {refDropdownOpen && refResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-border/80 rounded-lg shadow-lg max-h-48 overflow-auto">
                      {refResults.map(c => (
                        <button key={c.id} onClick={() => selectReferral(c)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--surface-2)]" style={{ transition: 'background-color 100ms' }}>
                          <span className="font-medium" style={{ color: 'var(--navy-ink)' }}>{c.name}</span>
                          <span className="text-xs ml-2" style={{ color: 'var(--ink-muted)' }}>{c.phone}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Style Profile Quick (for new clients) */}
          <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
            <CardHeader><CardTitle className="text-lg" style={{ color: 'var(--brand-navy)' }}>Style Profile</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <OptionalSelect label="Complexion Undertone" value={form.complexion_undertone} options={COMPLEXION_UNDERTONES}
                onChange={(v) => updateField('complexion_undertone', v)} />
              <OptionalSelect label="Complexion Depth" value={form.complexion_depth} options={COMPLEXION_DEPTHS}
                onChange={(v) => updateField('complexion_depth', v)} />
              <OptionalSelect label="Face Shape" value={form.face_shape} options={FACE_SHAPES}
                onChange={(v) => updateField('face_shape', v)} />
              <OptionalSelect label="Body Shape" value={form.body_shape} options={BODY_SHAPES}
                onChange={(v) => updateField('body_shape', v)} />
              <OptionalSelect label="Colour Preference" value={form.colour_preference} options={COLOUR_PREFS}
                onChange={(v) => updateField('colour_preference', v)} />
              <OptionalSelect label="Pattern Comfort" value={form.pattern_comfort} options={PATTERN_COMFORTS}
                onChange={(v) => updateField('pattern_comfort', v)} />
              <div className="space-y-1.5">
                <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Style Notes</Label>
                <textarea value={form.style_notes} onChange={(e) => updateField('style_notes', e.target.value)}
                  className="w-full h-20 px-3 py-2 rounded-lg border border-border/80 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:ring-offset-2" style={{ backgroundColor: 'white' }}
                  placeholder="Style observations..." />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function OptionalSelect({ label, value, options, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>{label}</Label>
      <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? '' : v)}>
        <SelectTrigger className="h-10 rounded-lg text-sm"><SelectValue placeholder="Not set" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Not set</SelectItem>
          {options.map(o => <SelectItem key={o} value={o} className="capitalize text-sm">{o.replace(/_/g, ' ')}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
